import React from "react"
import { Link } from "react-router-dom"
import { Home, Settings, Users, Star, Shield, Clock, Phone, Mail } from "lucide-react"

import { cn } from "../../lib/utils"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "./navigation-menu"

const hudsonServices = [
  {
    title: "Home Management",
    to: "/services",
    description: "Complete property oversight and maintenance coordination",
    icon: Home
  },
  {
    title: "Concierge Services", 
    to: "/services",
    description: "Personal assistance for all your lifestyle needs",
    icon: Star
  },
  {
    title: "Emergency Response",
    to: "/services",
    description: "24/7 rapid response for urgent home issues",
    icon: Shield
  },
  {
    title: "Seasonal Maintenance",
    to: "/services",
    description: "Proactive care throughout the changing seasons",
    icon: Clock
  }
]

const membershipTiers = [
  {
    title: "Premier Estate with Concierge — &lt;1500 SqFt",
    to: "/#pricing",
    description: "Monthly inspections, proactive maintenance, vendor management, seasonal care, dedicated manager, annual assessment, white glove service, full vendor coordination, maintenance scheduling, plus 12 hours of fix‑it work annually."
  },
  {
    title: "Premier Estate with Concierge — 1501-2999 SqFt", 
    to: "/#pricing",
    description: "Monthly inspections, proactive maintenance, vendor management, seasonal care, dedicated manager, annual assessment, white glove service, full vendor coordination, maintenance scheduling, plus 12 hours of fix‑it work annually."
  },
  {
    title: "Premier Estate with Concierge — 3000-4500 SqFt", 
    to: "/#pricing",
    description: "Monthly inspections, proactive maintenance, vendor management, seasonal care, dedicated manager, annual assessment, white glove service, full vendor coordination, maintenance scheduling, plus 12 hours of fix‑it work annually."
  },
  {
    title: "Premier Estate with Concierge — 4500+ SqFt", 
    to: "/#pricing",
    description: "Monthly inspections, proactive maintenance, vendor management, seasonal care, dedicated manager, annual assessment, white glove service, full vendor coordination, maintenance scheduling, plus 12 hours of fix‑it work annually."
  }
]

export function HudsonNavigationMenu() {
  return (
    <NavigationMenu>
      <NavigationMenuList>
        {/* Services Dropdown */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-gray-700 hover:text-gray-900">
            Services
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid gap-3 p-6 md:w-[500px] lg:w-[600px] lg:grid-cols-[1fr_1fr]">
              <li className="col-span-2 mb-4">
                <NavigationMenuLink asChild>
                  <Link
                    className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md"
                    to="/services"
                  >
                    <Home className="h-6 w-6 mb-2" />
                    <div className="mb-2 mt-4 text-lg font-semibold">
                      MyHome Services
                    </div>
                    <p className="text-sm leading-tight text-muted-foreground">
                      Effortless homeownership through comprehensive management and concierge services.
                    </p>
                  </Link>
                </NavigationMenuLink>
              </li>
              {hudsonServices.map((service) => (
                <HudsonListItem
                  key={service.title}
                  title={service.title}
                  to={service.to}
                  icon={service.icon}
                >
                  {service.description}
                </HudsonListItem>
              ))}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Membership Dropdown */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-gray-700 hover:text-gray-900">
            Membership
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid w-[400px] gap-3 p-6 md:w-[500px] lg:w-[600px]">
              <li className="mb-4">
                <NavigationMenuLink asChild>
                  <Link
                    className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-amber-50 to-amber-100 p-6 no-underline outline-none focus:shadow-md"
                    to="/membership"
                  >
                    <Users className="h-6 w-6 mb-2 text-amber-600" />
                    <div className="mb-2 mt-4 text-lg font-semibold text-amber-900">
                      Discover Home Membership
                    </div>
                    <p className="text-sm leading-tight text-amber-700">
                      Experience homeownership the way it was meant to be - effortless and refined.
                    </p>
                  </Link>
                </NavigationMenuLink>
              </li>
              {membershipTiers.map((tier) => (
                <HudsonListItem
                  key={tier.title}
                  title={tier.title}
                  to={tier.to}
                >
                  {tier.description}
                </HudsonListItem>
              ))}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Who is Hudson Link */}
        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link
              to="/who-is-hudson"
              className={cn(navigationMenuTriggerStyle(), "text-gray-700 hover:text-gray-900")}
            >
              Who is Hudson?
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>

        {/* Contact Link */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-gray-700 hover:text-gray-900">
            Contact
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="grid gap-3 p-6 w-[400px]">
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold mb-2">Get In Touch</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Ready to experience effortless homeownership? Contact us to learn more.
                  </p>
                </div>
                <div className="space-y-3">
                  <a 
                    href="tel:+15152164433" 
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    <div>
                      <div className="font-medium">Call Us</div>
                      <div className="text-sm text-muted-foreground">(515) 216-4433</div>
                    </div>
                  </a>
                  <a 
                    href="mailto:info@hudsonmyhome.com" 
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    <div>
                      <div className="font-medium">Email Us</div>
                      <div className="text-sm text-muted-foreground">info@hudsonmyhome.com</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

      </NavigationMenuList>
    </NavigationMenu>
  )
}

const HudsonListItem = React.forwardRef<
  React.ElementRef<typeof Link>,
  React.ComponentPropsWithoutRef<typeof Link> & {
    title: string
    children: React.ReactNode
    icon?: React.ComponentType<{ className?: string }>
  }
>(({ className, title, children, to, icon: Icon, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          ref={ref}
          to={to || ""}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className
          )}
          {...props}
        >
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
            <div className="text-sm font-medium leading-none">{title}</div>
          </div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  )
})
HudsonListItem.displayName = "HudsonListItem"

export default HudsonNavigationMenu 